// ─── Display value helpers ─────────────────────────────────────────────────────

export const formatINR = (n) => {
  if (n == null || n === "" || isNaN(Number(n))) return null;
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

export const getCheckerAmount = (ele) =>
  (ele?.checkerInputChargeAmount !== 0
    ? ele?.checkerInputChargeAmount
    : null) ??
  ele?.makerInputChargeAmount ??
  ele?.userInputChargeAmount;

export const getMakerAmount = (ele) =>
  (ele?.makerInputChargeAmount !== 0 ? ele?.makerInputChargeAmount : null) ??
  ele?.userInputChargeAmount;

export const getPaymentAmount = (ele) =>
  ele?.checkerInputChargeAmount ??
  ele?.makerInputChargeAmount ??
  ele?.userInputChargeAmount;

export const getDisplayValue = (ele, role) => {
  const status = ele?.status;

  if (role === "MAKER") {
    if (status === "PAYMENT") return getPaymentAmount(ele);
    if (status === "CHECKER") return getCheckerAmount(ele);
    return ele?.makerInputChargeAmount || ele?.userInputChargeAmount;
  }

  if (role === "CHECKER") return getCheckerAmount(ele);

  if (status === "PAYMENT") return getPaymentAmount(ele);
  if (status === "CHECKER") return getCheckerAmount(ele);
  if (status === "MAKER") return getMakerAmount(ele);

  return ele?.userInputChargeAmount ?? ele?.amount;
};

// ─── Data processing ───────────────────────────────────────────────────────────

export const processConsumerViewData = (responseData, scheduleData, role) => {
  const common = {
    scheduleId: scheduleData?.scheduleId,
    makerId: null,
    checkerId: null,
    actualChargeAmount: null,
    makerInputChargeAmount: null,
    checkerInputChargeAmount: null,
    isView: true,
  };

  const createCharge = (base, overrides) => ({ ...base, ...common, ...overrides });

  const traderGenCharges = (responseData?.generatorChargesBreakDown || []).filter(
    (charge) => charge?.chargeType === "Trader",
  );
  const hasTraderCharge = traderGenCharges.length > 0;

  const energyChargeUserInput = (
    (responseData?.totalAmountToGenerator * scheduleData?.deliveredVolume) /
    scheduleData?.scheduleVolume
  )?.toFixed(3);

  const traderEntityId = traderGenCharges[0]?.toEntityId;

  const genDetails = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: hasTraderCharge ? "Trader" : responseData?.generatorName || "Generator",
    fromUserRole: "CONSUMER",
    toUserRole: hasTraderCharge ? "Trader" : "GENERATOR",
    ...(hasTraderCharge && {
      fromUserId: scheduleData?.selfPartyEntityId,
      toUserId: traderEntityId,
    }),
    name: "Energy Charge",
    frequency: "RECURRING",
    type: "PER_MW",
    value:
      scheduleData?.calculatedDamPrice?.toFixed(3) || scheduleData?.energyRate,
    isAdminCharge: false,
    amount: responseData?.totalAmountToGenerator?.toFixed(3),
    userInputChargeAmount: energyChargeUserInput,
  });

  let sellerAmountCharge = null;
  if (hasTraderCharge) {
    const traderTotalAmount = traderGenCharges.reduce(
      (acc, c) => acc + (parseFloat(c?.amount) || 0),
      0,
    );
    const traderTotalUserInput = traderGenCharges.reduce((acc, c) => {
      const val =
        c?.type === "FIXED"
          ? parseFloat(c?.amount) || 0
          : (parseFloat(c?.amount) * scheduleData?.deliveredVolume) /
              scheduleData?.scheduleVolume || 0;
      return acc + val;
    }, 0);

    const traderTotalChargeValue = traderGenCharges.reduce(
      (acc, c) => acc + (parseFloat(c?.chargeValue ?? c?.value) || 0),
      0,
    );
    const energyRate =
      parseFloat(scheduleData?.calculatedDamPrice) ||
      parseFloat(scheduleData?.energyRate) ||
      0;
    const sellerAmountValue = (energyRate - traderTotalChargeValue).toFixed(3);

    const sellerAmt =
      (parseFloat(responseData?.totalAmountToGenerator) || 0) - traderTotalAmount;
    const sellerAmtUserInput =
      (parseFloat(energyChargeUserInput) || 0) - traderTotalUserInput;

    sellerAmountCharge = createCharge(
      {
        fromUser: "Trader",
        toUser: responseData?.generatorName || "Generator",
        fromUserRole: "Trader",
        toUserRole: "GENERATOR",
        fromUserId: traderEntityId,
        toUserId: scheduleData?.counterPartyEntityId,
        name: "Seller Amount",
        frequency: "RECURRING",
        type: "PER_MW",
        value: sellerAmountValue,
        isAdminCharge: false,
        amount: sellerAmt?.toFixed(3),
        userInputChargeAmount: sellerAmtUserInput?.toFixed(3),
        isNonEditable: true,
      },
      { isView: true },
    );
  }

  const processedCharges = (responseData?.consumerChargesBreakDown || []).map(
    (charge) =>
      createCharge(charge, {
        fromUser: responseData?.consumerName || "Consumer",
        toUser: charge?.chargeType,
        fromUserRole: "CONSUMER",
        toUserRole: charge?.chargeType || "OTHERS",
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
      }),
  );

  const processedChargesGen = (responseData?.generatorChargesBreakDown || [])
    .filter((charge) => charge?.chargeType !== "Trader")
    .map((charge) =>
      createCharge(charge, {
        fromUser: responseData?.generatorName || "Generator",
        toUser: charge?.chargeType,
        fromUserRole: "GENERATOR",
        toUserRole: charge?.chargeType || "OTHERS",
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
        isView: false,
      }),
    );

  const processedTraderCharges = hasTraderCharge
    ? traderGenCharges.map((charge) =>
        createCharge(charge, {
          fromUser: responseData?.consumerName || "Consumer",
          toUser: "Trader",
          fromUserRole: "CONSUMER",
          toUserRole: "Trader",
          fromUserId: scheduleData?.selfPartyEntityId,
          toUserId: traderEntityId,
          isNonEditable: charge?.isAdminCharge,
          userInputChargeAmount:
            charge?.type === "FIXED"
              ? charge?.amount
              : (
                  (charge?.amount * scheduleData?.deliveredVolume) /
                  scheduleData?.scheduleVolume
                )?.toFixed(3),
          isView: role === "TRADER",
        }),
      )
    : [];

  const calculateRefund = (items) =>
    items.reduce((acc, item) => {
      const original = item.amount || 0;
      const calculated = item.userInputChargeAmount || 0;
      return acc + (original - calculated);
    }, 0);

  const consumerRefundTotal = calculateRefund([genDetails, ...processedCharges]);
  const generatorRefundTotal = calculateRefund(processedChargesGen);

  const refund = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: responseData?.consumerName || "Consumer",
    fromUserRole: "CONSUMER",
    tpToUserRole: "CONSUMER",
    name: "Refund Request",
    frequency: "RECURRING",
    type: "FIXED",
    amount: 0,
    userInputChargeAmount: consumerRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundGen = createCharge(refund, {
    fromUser: responseData?.generatorName || "Generator",
    toUser: responseData?.generatorName || "Generator",
    fromUserRole: "GENERATOR",
    tpToUserRole: "GENERATOR",
    name: "Refund Request",
    isView: false,
    amount: generatorRefundTotal?.toFixed(3),
    userInputChargeAmount: generatorRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundConMargin = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: responseData?.consumerName || "Consumer",
    fromUserRole: "CONSUMER",
    tpToUserRole: "CONSUMER",
    name: "Extra Margin",
    frequency: "RECURRING",
    type: "FIXED",
    amount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  const refundGenMargin = createCharge(refund, {
    fromUser: responseData?.generatorName || "Generator",
    toUser: responseData?.generatorName || "Generator",
    fromUserRole: "GENERATOR",
    tpToUserRole: "GENERATOR",
    name: "Extra Margin",
    isView: false,
    amount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  return [
    genDetails,
    ...(sellerAmountCharge ? [sellerAmountCharge] : []),
    ...processedTraderCharges,
    ...processedCharges,
    refundConMargin,
    refund,
    ...processedChargesGen,
    refundGen,
    refundGenMargin,
  ];
};

export const processAdminViewData = (charges, role) => {
  if (!charges) return [];
  return charges.map((charge) => {
    let updatedCharge = { ...charge, isView: true };
    if (role === "MAKER") {
      updatedCharge.makerInputChargeAmount =
        charge?.makerInputChargeAmount !== 0
          ? charge?.makerInputChargeAmount
          : charge?.userInputChargeAmount || 0;
    } else if (role === "CHECKER") {
      updatedCharge.checkerInputChargeAmount =
        charge?.checkerInputChargeAmount !== 0
          ? charge?.checkerInputChargeAmount
          : charge?.makerInputChargeAmount || 0;
    }
    return updatedCharge;
  });
};

export const prepareSubmissionPayload = ({
  details,
  role,
  userId,
  scheduleId,
  isReject = false,
  data,
  selectedScheduleId,
  consumerWalletId,
  generatorWalletId,
}) => {
  return details
    .filter((ele) => ele?.chargeType !== "Trader")
    .map((ele) => {
    const isMaker = role === "MAKER";
    const isChecker = role === "CHECKER";

    let status = "MAKER";
    if (!isReject && isMaker) status = "CHECKER";
    else if (!isReject && isChecker) status = "PAYMENT";

    const selfId = data?.selfPartyEntityId;
    const counterId = data?.counterPartyEntityId;

    let fromUserId = ele?.fromUserRole === "CONSUMER" ? selfId : counterId;
    let toUserId;
    if (ele?.fromUserRole === "CONSUMER" && ele?.toUserRole === "GENERATOR") {
      toUserId = ele?.fromUserRole === "CONSUMER" ? counterId : selfId;
    } else if (ele?.fromUserRole === ele?.toUserRole) {
      toUserId = ele?.fromUserRole === "CONSUMER" ? selfId : counterId;
    } else {
      toUserId = ele?.toEntityId;
    }

    const resolvedFromUserId = ele?.fromUserId || fromUserId;
    const resolvedToUserId =
      ele?.fromUser === ele?.toUser
        ? resolvedFromUserId
        : ele?.toUserId || ele?.toEntityId || toUserId;

    return {
      id: isMaker || isChecker ? ele?.id : null,
      fromUser: ele?.fromUser,
      fromUserId: resolvedFromUserId,
      toUser: ele?.toUser,
      toUserId: resolvedToUserId,
      chargeName: ele?.name || ele?.chargeName,
      chargeFrequency: ele?.frequency || ele?.chargeFrequency,
      chargeType: ele?.type || ele?.chargeType,
      isAdminCharge: ele?.isAdminCharge || false,
      chargeAmount:
        ele?.amount || ele?.chargeAmount || ele?.userInputChargeAmount || 0,
      scheduleId,
      chargeValue: ele?.value || ele?.chargeValue || 0,
      makerId: isMaker || ele?.makerId ? ele?.makerId || userId : null,
      checkerId: isChecker || ele?.checkerId ? ele?.checkerId || userId : null,
      actualChargeAmount: ele?.actualChargeAmount || ele?.amount,
      userInputChargeAmount: ele?.userInputChargeAmount || 0,
      makerInputChargeAmount: ele?.makerInputChargeAmount,
      checkerInputChargeAmount: isMaker ? 0 : ele?.checkerInputChargeAmount,
      status,
      repScheduleId: selectedScheduleId || ele?.repScheduleId,
      makerComment: ele?.makerComment,
      checkerComment: ele?.checkerComment,
      bearer:
        ele?.bearer ||
        (ele?.fromUserRole === "CONSUMER"
          ? "BUYER"
          : ele?.fromUserRole === "GENERATOR"
            ? "SELLER"
            : "APPLICANT"),
      payer:
        ele?.payer ||
        (ele?.toUserRole === "CONSUMER"
          ? "BUYER"
          : ele?.toUserRole === "GENERATOR"
            ? "SELLER"
            : "APPLICANT"),
      creditWalletAccountId:
        ele?.creditWalletAccountId ||
        (ele?.toUserRole === "CONSUMER"
          ? consumerWalletId
          : ele?.toUserRole === "GENERATOR"
            ? generatorWalletId
            : null),
      debitWalletAccountId:
        ele?.debitWalletAccountId ||
        (ele?.fromUserRole === "CONSUMER"
          ? consumerWalletId
          : ele?.fromUserRole === "GENERATOR"
            ? generatorWalletId
            : null),
    };
  });
};
